import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SharedPageLayout from '../components/SharedPageLayout';
import { Mail, Phone, MapPin, Upload, Settings, Eye, DraftingCompass, Layers, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { trackContactFormSubmission } from '../utils/analytics';
import { BACKEND_URL } from '../config';

const API = `${BACKEND_URL}/api`;


export const ServicesRoute = () => {
  const navigate = useNavigate();
  const [expandedCards, setExpandedCards] = useState({});

  const toggleCard = (cardId) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };
  
  // Handle hash-based navigation when component mounts or hash changes
  React.useEffect(() => {
    const hash = window.location.hash.substring(1); // Remove the # symbol
    if (hash) {
      // Small delay to ensure the page has rendered
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          // Get element position and scroll with offset to show title
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 100; // 100px offset to show title and some space above
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);
  
  return (
    <SharedPageLayout currentPage="services">
      {/* Hero Banner - Background Image with Blue Overlay */}
      <div className="relative text-white overflow-hidden">
        
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_bdaed582-c4d0-4d99-8e9e-aa6015fcd0f5/artifacts/a6i1rzr9_Services%20Page%20Hero%20background.jpeg)' }}
        ></div>
        
        {/* Blue overlay to match brand colors */}
        <div className="absolute inset-0 bg-[#0007b5]/80"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-20 md:py-28">
            <div className="max-w-4xl">
              <p className="text-blue-200 text-sm font-medium tracking-widest uppercase mb-6">Professional Services</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight font-heading mb-6">
                Our Services
              </h1>
              <p className="text-lg md:text-xl text-blue-100 max-w-2xl leading-relaxed">
                Engineering-driven 3D printing solutions backed by 8+ years of experience
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Core Services with Image */}
        <section className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <p className="text-xl text-slate-700 font-medium leading-relaxed mb-4">
                We know there is a lot that goes into a finished part.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Our services include <span className="font-bold text-[#0007b5]">on-demand printing, transforming your idea into a finished part, or anywhere in between</span>. We can support you at any stage in the production cycle or take the entire project off your hands.
              </p>
              <div className="w-32 h-0.5 bg-[#0007b5]/30 my-6"></div>
              <p className="text-lg text-slate-600 leading-relaxed">
                From initial conceptualization to final production, our comprehensive services can cover any or every stage of your project. Whether you're a hobbyist, entrepreneur, or enterprise, our comprehensive offerings provide you the expertise, support, and production to complete your 3D printing projects.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_4d5d21e8-e723-4374-8b92-bc54b14f6d43/artifacts/2v7h6qm1_Services%20Illustration%203.png" 
                alt="3D Printing Production Cycle - Conceptualization, Design, Prototyping, Optimizing, Final 3D Printing"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="space-y-6">
            {/* 3D File Printing Card */}
            <div id="3d-printing" className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100 overflow-hidden">
              <div 
                className="p-8 cursor-pointer"
                onClick={() => toggleCard('3d-printing')}
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Upload className="w-8 h-8 text-[#0007b5]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">On Demand Printing</h3>
                      <ChevronDown className={`w-6 h-6 text-slate-400 transition-transform duration-300 ${expandedCards['3d-printing'] ? 'rotate-180' : ''}`} />
                    </div>
                    <p className="text-lg text-slate-600 leading-relaxed">
                      Get an instant quote based on an intelligent automated analysis of your CAD file and choose from our recommended, preset printing settings.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Fade hint - only show when collapsed */}
              {!expandedCards['3d-printing'] && (
                <div className="h-10 bg-gradient-to-t from-blue-50/80 to-transparent flex items-end justify-center pb-2">
                  <span className="text-xs text-[#0007b5] font-medium">Click to learn more</span>
                </div>
              )}
              
              <div className={`overflow-hidden transition-all duration-500 ease-in-out ${expandedCards['3d-printing'] ? 'max-h-[1500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-8 pb-8 pt-0 ml-22">
                  <div className="border-t border-slate-200 pt-6 ml-[88px]">
                    <h4 className="font-semibold text-slate-900 mb-4">What's Included:</h4>
                    <ul className="space-y-3 text-slate-600 mb-6">
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>CAD File analysis and 3D Preview for quick validation</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Powerful CAD editing tools including scaling and orientation available</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Carefully selected preset print settings and material selection based on your project requirements</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Post-processing options including smoothing, painting, and finishing</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Fast turnaround times with production time estimates before order placement</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Comprehensive professional review of your CAD file and print settings chosen for each on-demand print order</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Your IP is fully protected and retained</span>
                      </li>
                    </ul>
                    
                    <h4 className="font-semibold text-slate-900 mb-4">Materials Available:</h4>
                    <p className="text-slate-600 mb-6">
                      We offer a comprehensive range of FDM materials including PLA, PLA+, Silk PLA, PETG, ABS, ASA, TPU (flexible), 
                      Carbon Fiber reinforced filaments (PLA-CF, PETG-CF, PPS-CF), Wood-fill, Metal-fill, and high-temperature materials. Each material is selected 
                      based on your specific requirements for strength, flexibility, temperature resistance, and surface finish.
                    </p>
                    
                    <h4 className="font-semibold text-slate-900 mb-4">Quality Options:</h4>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Concept</span>
                        <p className="text-sm text-slate-600">Fast prints for initial form and fit checks</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Prototype</span>
                        <p className="text-sm text-slate-600">Balanced quality for functional testing</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Production</span>
                        <p className="text-sm text-slate-600">High quality for end-use parts</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Display</span>
                        <p className="text-sm text-slate-600">Premium finish for presentation pieces</p>
                      </div>
                    </div>
                    
                    <h4 className="font-semibold text-slate-900 mb-4">Supported File Formats:</h4>
                    <p className="text-slate-600">
                      STL, 3MF, OBJ, STEP, IGES, and most common CAD formats. We can also work with native files from 
                      SolidWorks, Fusion 360, AutoCAD, and other professional CAD software.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Design Services Card */}
            <div id="design" className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100 overflow-hidden">
              <div 
                className="p-8 cursor-pointer"
                onClick={() => toggleCard('design')}
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <DraftingCompass className="w-8 h-8 text-[#0007b5]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">Design Services</h3>
                      <ChevronDown className={`w-6 h-6 text-slate-400 transition-transform duration-300 ${expandedCards['design'] ? 'rotate-180' : ''}`} />
                    </div>
                    <p className="text-lg text-slate-600 leading-relaxed">
                      Complete design-to-production services with engineering expertise in functional, cosmetic, and load-bearing applications.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Fade hint - only show when collapsed */}
              {!expandedCards['design'] && (
                <div className="h-10 bg-gradient-to-t from-blue-50/80 to-transparent flex items-end justify-center pb-2">
                  <span className="text-xs text-[#0007b5] font-medium">Click to learn more</span>
                </div>
              )}
              
              <div className={`overflow-hidden transition-all duration-500 ease-in-out ${expandedCards['design'] ? 'max-h-[1500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-8 pb-8 pt-0">
                  <div className="border-t border-slate-200 pt-6 ml-[88px]">
                    <h4 className="font-semibold text-slate-900 mb-4">What's Included:</h4>
                    <ul className="space-y-3 text-slate-600 mb-6">
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Custom product design from concept sketches or verbal descriptions</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Functional prototyping and iterative design refinement</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Your IP is fully protected and retained</span>
                      </li>
                    </ul>
                    
                    <h4 className="font-semibold text-slate-900 mb-4">Design Capabilities:</h4>
                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Mechanical Parts</span>
                        <p className="text-sm text-slate-600">Gears, brackets, housings, enclosures, and functional assemblies</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Consumer Products</span>
                        <p className="text-sm text-slate-600">Product housings, ergonomic designs, and aesthetic components</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Custom Fixtures</span>
                        <p className="text-sm text-slate-600">Jigs, fixtures, tooling, and manufacturing aids</p>
                      </div>
                    </div>
                    
                    <h4 className="font-semibold text-slate-900 mb-4">Engineering Analysis:</h4>
                    <p className="text-slate-600 mb-6">
                      Our engineering team can perform structural analysis, thermal stress testing, and fitment verification 
                      to ensure your designs meet performance requirements. We consider factors like material properties, 
                      print orientation, mounting points, cyclical loads, thermal stress, infill patterns, and wall thickness to optimize for strength and durability.
                    </p>
                    
                    <h4 className="font-semibold text-slate-900 mb-4">Deliverables:</h4>
                    <ul className="space-y-2 text-slate-600">
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Native CAD files (STEP, Fusion 360, SolidWorks compatible)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Print-ready files with optimized settings</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Material and print setting recommendations</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Printed prototypes or finished components packed and delivered (optional)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Technical drawings and specifications (optional)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 2D to 3D Conversion Card */}
            <div id="conversion" className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100 overflow-hidden">
              <div 
                className="p-8 cursor-pointer"
                onClick={() => toggleCard('conversion')}
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Layers className="w-8 h-8 text-[#0007b5]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">2D to 3D Conversion</h3>
                      <ChevronDown className={`w-6 h-6 text-slate-400 transition-transform duration-300 ${expandedCards['conversion'] ? 'rotate-180' : ''}`} />
                    </div>
                    <p className="text-lg text-slate-600 leading-relaxed">
                      Expert engineering services to transform sketches, 2D CAD designs, and technical or shop drawings into optimized 3D models ready for 3D printing production.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Fade hint - only show when collapsed */}
              {!expandedCards['conversion'] && (
                <div className="h-10 bg-gradient-to-t from-blue-50/80 to-transparent flex items-end justify-center pb-2">
                  <span className="text-xs text-[#0007b5] font-medium">Click to learn more</span>
                </div>
              )}
              
              <div className={`overflow-hidden transition-all duration-500 ease-in-out ${expandedCards['conversion'] ? 'max-h-[1500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-8 pb-8 pt-0">
                  <div className="border-t border-slate-200 pt-6 ml-[88px]">
                    <h4 className="font-semibold text-slate-900 mb-4">What's Included:</h4>
                    <ul className="space-y-3 text-slate-600 mb-6">
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Accurate conversion from 2D drawings, blueprints, sketches, or technical drawings</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Engineering-grade CAD modeling with proper dimensions and tolerances</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Your IP is fully protected and retained</span>
                      </li>
                    </ul>
                    
                    <h4 className="font-semibold text-slate-900 mb-4">Optional Services:</h4>
                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Printable Design</span>
                        <p className="text-sm text-slate-600">Design for manufacturability (DFM) optimization for 3D printing</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Consulting</span>
                        <p className="text-sm text-slate-600">Consulting on your design for component optimization opportunities where possible</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Prototype</span>
                        <p className="text-sm text-slate-600">3D printing a prototype of the created 3D model for review and test fitment</p>
                      </div>
                    </div>
                    
                    <h4 className="font-semibold text-slate-900 mb-4">When to consider 2D to 3D Conversion:</h4>
                    <ul className="space-y-2 text-slate-600 mb-6">
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Convert drawings or blueprints of an existing component design to a 3D model</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>There is a sketch or a concept drawing and you need it converted to a 3D model</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Create a 3D model from technical specifications or manuals</span>
                      </li>
                    </ul>
                    
                    <h4 className="font-semibold text-slate-900 mb-4">Accepted Input Formats:</h4>
                    <p className="text-slate-600">
                      PDF drawings, scanned blueprints, hand sketches (photographed), DWG/DXF files, 
                      technical specifications, and even verbal descriptions with reference images.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Optimization Card */}
            <div id="optimization" className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100 overflow-hidden">
              <div 
                className="p-8 cursor-pointer"
                onClick={() => toggleCard('optimization')}
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Settings className="w-8 h-8 text-[#0007b5]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">Product Optimization</h3>
                      <ChevronDown className={`w-6 h-6 text-slate-400 transition-transform duration-300 ${expandedCards['optimization'] ? 'rotate-180' : ''}`} />
                    </div>
                    <p className="text-lg text-slate-600 leading-relaxed">
                      Engineering analysis and component optimization for improved performance, durability, and printing results as well as consistency.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Fade hint - only show when collapsed */}
              {!expandedCards['optimization'] && (
                <div className="h-10 bg-gradient-to-t from-blue-50/80 to-transparent flex items-end justify-center pb-2">
                  <span className="text-xs text-[#0007b5] font-medium">Click to learn more</span>
                </div>
              )}
              
              <div className={`overflow-hidden transition-all duration-500 ease-in-out ${expandedCards['optimization'] ? 'max-h-[1500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-8 pb-8 pt-0">
                  <div className="border-t border-slate-200 pt-6 ml-[88px]">
                    <h4 className="font-semibold text-slate-900 mb-4">What's Included:</h4>
                    <ul className="space-y-3 text-slate-600 mb-6">
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Modification or optimization for print orientation and support structure</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Structural reinforcement for load-bearing and high-stress applications</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Weight reduction where possible through structure and material optimization</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Model splitting for large components or assemblies and structures</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Your IP is fully protected and retained</span>
                      </li>
                    </ul>
                    
                    <h4 className="font-semibold text-slate-900 mb-4">Optimization Benefits:</h4>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Weight Reduction</span>
                        <p className="text-sm text-slate-600">Reduce material usage while maintaining structural integrity through advanced lattice and topology optimization</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Strength Enhancement</span>
                        <p className="text-sm text-slate-600">Strategic reinforcement and material selection to improve load-bearing capacity and impact resistance</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Print Optimization</span>
                        <p className="text-sm text-slate-600">Optimal orientation, support minimization, and layer adhesion improvements for better print quality</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Cost Reduction</span>
                        <p className="text-sm text-slate-600">Material efficiency improvements and print time optimization to reduce per-part costs</p>
                      </div>
                    </div>
                    
                    <h4 className="font-semibold text-slate-900 mb-4">When to Consider Optimization:</h4>
                    <ul className="space-y-2 text-slate-600">
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Your existing design wasn't created with 3D printing in mind</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Parts are failing or not meeting performance requirements</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>You need to reduce weight for aerospace, automotive, or portable applications</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Production costs need to be reduced for volume manufacturing</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>You have a model or part that doesn't fit on your printer bed</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Realistic Renders Card */}
            <div id="renders" className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100 overflow-hidden">
              <div 
                className="p-8 cursor-pointer"
                onClick={() => toggleCard('renders')}
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Eye className="w-8 h-8 text-[#0007b5]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">Model & Product Renders</h3>
                      <ChevronDown className={`w-6 h-6 text-slate-400 transition-transform duration-300 ${expandedCards['renders'] ? 'rotate-180' : ''}`} />
                    </div>
                    <p className="text-lg text-slate-600 leading-relaxed">
                      Realistic visualization services to showcase your product designs before manufacturing, perfect for presentations and marketing.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Fade hint - only show when collapsed */}
              {!expandedCards['renders'] && (
                <div className="h-10 bg-gradient-to-t from-blue-50/80 to-transparent flex items-end justify-center pb-2">
                  <span className="text-xs text-[#0007b5] font-medium">Click to learn more</span>
                </div>
              )}
              
              <div className={`overflow-hidden transition-all duration-500 ease-in-out ${expandedCards['renders'] ? 'max-h-[1500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-8 pb-8 pt-0">
                  <div className="border-t border-slate-200 pt-6 ml-[88px]">
                    <h4 className="font-semibold text-slate-900 mb-4">What's Included:</h4>
                    <ul className="space-y-3 text-slate-600 mb-6">
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>High-quality photorealistic renders with accurate materials and lighting</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Multiple viewing angles and environmental settings for comprehensive visualization</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Product animation and 360-degree rotation videos available</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Marketing-ready images for presentations, websites, and promotional materials</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Your IP is fully protected and retained</span>
                      </li>
                    </ul>
                    
                    <h4 className="font-semibold text-slate-900 mb-4">Render Services:</h4>
                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Product Pictures</span>
                        <p className="text-sm text-slate-600">Static renders from different angles with studio lighting</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Lifestyle Pictures</span>
                        <p className="text-sm text-slate-600">Lifestyle/Environmental renders to showcase product in realistic scene/setting</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <span className="font-medium text-slate-900">Animations</span>
                        <p className="text-sm text-slate-600">360° turntable or product assembly and demonstration animations</p>
                      </div>
                    </div>
                    
                    <h4 className="font-semibold text-slate-900 mb-4">Use Cases:</h4>
                    <ul className="space-y-2 text-slate-600">
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Crowdfunding campaigns and investor presentations</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>E-commerce product listings and marketing materials</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Design validation before committing to physical prototypes</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Client presentations and design approval processes</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#0007b5] mr-2">•</span>
                        <span>Visualizing 3D printed end-use parts for aesthetic validation</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Experience Section with Visual Showcase */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">A Ton of Experience. Literally.</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our engineering team has hands-on experience printing over 3,000+ pounds of material 
              across a comprehensive range of use cases and applications.
            </p>
          </div>

          {/* Featured Image Grid and Use Case Cards */}
          <div className="bg-slate-50 rounded-3xl p-12">
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src="/images/services-prototyping.jpg" 
                  alt="Prototype Testing"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src="/images/services-quality.jpg" 
                  alt="3D Printed Parts"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src="/services_functional_picture_3.jpg" 
                  alt="Precision Mechanical Components"
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-slate-100 hover:border-[#0007b5]/20">
              <div className="text-2xl font-bold text-[#0007b5] mb-2">Prototype</div>
              <p className="text-slate-700 mb-4">
                Rapid iteration and testing phases for product development, functional validation, 
                and design verification with quick turnaround times.
              </p>
              <div className="text-sm text-slate-500">Applications: Concept validation, fitment testing, form studies</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-slate-100 hover:border-[#0007b5]/20">
              <div className="text-2xl font-bold text-[#0007b5] mb-2">Cosmetic</div>
              <p className="text-slate-700 mb-4">
                Display-quality parts with smoothed surfaces, professional finishes, 
                painting preparation, and presentation-ready aesthetics.
              </p>
              <div className="text-sm text-slate-500">Applications: Trade shows, marketing materials, visual prototypes</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-slate-100 hover:border-[#0007b5]/20">
              <div className="text-2xl font-bold text-[#0007b5] mb-2">Functional</div>
              <p className="text-slate-700 mb-4">
                Working components that integrate into assemblies, mechanisms, 
                and systems with proper dimensional accuracy and material properties.
              </p>
              <div className="text-sm text-slate-500">Applications: Jigs, fixtures, brackets, housings</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-slate-100 hover:border-[#0007b5]/20">
              <div className="text-2xl font-bold text-[#0007b5] mb-2">Load Bearing</div>
              <p className="text-slate-700 mb-4">
                Structural components designed to handle mechanical stress, 
                weight, and operational forces with engineering validation.
              </p>
              <div className="text-sm text-slate-500">Applications: Brackets, supports, structural elements</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-slate-100 hover:border-[#0007b5]/20">
              <div className="text-2xl font-bold text-[#0007b5] mb-2">Post-Processed</div>
              <p className="text-slate-700 mb-4">
                Smoothed, sanded, and finished parts ready for painting, 
                coating, or wrapping applications with professional surface quality.
              </p>
              <div className="text-sm text-slate-500">Applications: Finished products, painted parts, wrapped components</div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-slate-100 hover:border-[#0007b5]/20">
              <div className="text-2xl font-bold text-[#0007b5] mb-2">Production</div>
              <p className="text-slate-700 mb-4">
                End-use parts for manufacturing, tooling, and production applications 
                with consistent quality and dimensional accuracy.
              </p>
              <div className="text-sm text-slate-500">Applications: End-use parts, tooling, manufacturing aids</div>
            </div>
          </div>
          </div>
        </section>

        {/* Why Choose Us with Image */}
        <section className="mb-20">
          <div className="mb-12">
            <div className="text-[#0007b5] text-sm font-medium tracking-widest uppercase mb-3">We'll take care of that</div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-4">
              Take The Guesswork Out
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl">
              Engineering expertise meets manufacturing flexibility for results you can trust.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_4d5d21e8-e723-4374-8b92-bc54b14f6d43/artifacts/wt4tqnon_PETG-CF%20Functional%20Bracket%202.jpg" 
                  alt="PETG-CF Functional Bracket"
                  className="rounded-2xl w-full h-auto shadow-xl"
                />
              </div>
            </div>
            <div className="space-y-8 order-1 md:order-2">
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Engineering-First Approach</h3>
                <p className="text-slate-700">
                  Created by engineers who understand the technical challenges of 3D printing. 
                  We don't just print - we analyze, optimize, and engineer solutions.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Proven Track Record</h3>
                <p className="text-slate-700">
                  Over 3,000 lb. of material printed across a comprehensive range of use cases and applications. 
                  From delicate prototypes to heavy-duty production components.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Complete Flexibility</h3>
                <p className="text-slate-700">
                  Whether you need rapid prototypes, cosmetic displays, functional parts, 
                  or production components - we have the experience to deliver.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Material Expertise</h3>
                <p className="text-slate-700">
                  Deep knowledge of material properties, applications, and limitations. 
                  We recommend the right material for your specific requirements.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center bg-gradient-to-r from-slate-900 to-blue-900 rounded-3xl p-12 text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Experience the difference that engineering expertise makes. Get an instant quote 
            or contact us to discuss your project requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const quoteSection = document.querySelector('#instant-quote');
                  if (quoteSection) {
                    quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100);
              }}
              className="bg-white text-[#0007b5] hover:bg-blue-50 px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Get Instant Quote
            </button>
            <button
              onClick={() => {
                navigate('/contact');
                setTimeout(() => {
                  const helpSection = document.querySelector('#here-to-help');
                  if (helpSection) {
                    const yOffset = -80;
                    const y = helpSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                  }
                }, 100);
              }}
              className="border border-blue-200 text-white hover:bg-blue-800 px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Contact Us
            </button>
          </div>
        </section>

      </div>
    </SharedPageLayout>
  );
};


export const ContactRoute = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const API_BASE = BACKEND_URL;
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Thank you for contacting us! We will get back to you within 24 hours.' });
        
        // Track contact form submission in Google Analytics
        trackContactFormSubmission('Contact Page Form');
        
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus({ type: 'error', message: 'Failed to send message. Please try again or email us directly.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Failed to send message. Please try again or email us directly.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SharedPageLayout currentPage="contact">
      {/* Hero Banner - Background Image with Blue Overlay */}
      <div className="relative text-white overflow-hidden">
        
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_1d797660-bdd3-4967-b006-914724c6ec3a/artifacts/9o2twqsd_about-us-cover-2.jpg)' }}
        ></div>
        
        {/* Blue overlay to match brand colors */}
        <div className="absolute inset-0 bg-[#0007b5]/80"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-20 md:py-28">
            <div className="max-w-4xl">
              <p className="text-blue-200 text-sm font-medium tracking-widest uppercase mb-6">Get In Touch</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight font-heading mb-6">
                Contact Us
              </h1>
              <p className="text-lg md:text-xl text-blue-100 max-w-2xl leading-relaxed">
                Get in touch with our engineering team—we&apos;re here to help bring your project to life
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Contact Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 max-w-3xl mx-auto">
          <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                <Mail className="w-8 h-8 text-[#0007b5]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Email Us</h3>
              <p className="text-slate-600 text-lg">hello@create3dworks.com</p>
              <p className="text-slate-500 text-sm mt-1">Same Day Response</p>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center rounded-2xl">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
                <MapPin className="w-8 h-8 text-[#0007b5]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Visit Us</h3>
              <p className="text-slate-600 text-lg">590 Portland St - Unit 117<br />Dartmouth, NS B2W 6B7<br />Canada</p>
              <p className="text-slate-500 text-sm mt-1">Showroom hours coming soon!</p>
            </CardContent>
          </Card>
        </div>

        {/* Professional Message Section */}
        <div id="here-to-help" className="max-w-4xl mx-auto mb-16">
          <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8 text-center border border-blue-100">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">We're Here to Help</h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              Whether you have questions about our services, need technical guidance, or want to discuss 
              your project requirements, our engineering team is ready to provide expert support.
            </p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-3xl mx-auto">
          <Card className="shadow-xl border-0 rounded-2xl">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-t-2xl">
              <CardTitle className="text-3xl font-bold text-slate-900">Send Us a Message</CardTitle>
              <p className="text-slate-600 mt-3 text-lg">Fill out the form below and we'll get back to you within 2 hours during business hours.</p>
            </CardHeader>
            <CardContent className="pt-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    type="text"
                    placeholder="What is this regarding?"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <textarea
                    id="message"
                    rows="6"
                    placeholder="Tell us more about your project or inquiry..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {submitStatus && (
                  <div className={`p-4 rounded-lg ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-50 border border-green-200 text-green-800' 
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#0007b5] to-[#000690] hover:from-blue-700 hover:to-blue-800 text-white py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Trust Elements */}
          <div className="mt-12 grid md:grid-cols-3 gap-6 text-center">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-[#0007b5] font-bold text-3xl mb-2">2 Hours</div>
              <div className="text-slate-600">Average Response Time</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-[#0007b5] font-bold text-3xl mb-2">24/7</div>
              <div className="text-slate-600">Email Support Available</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-[#0007b5] font-bold text-3xl mb-2">100%</div>
              <div className="text-slate-600">Satisfaction Guaranteed</div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="max-w-4xl mx-auto mt-16 bg-slate-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">Prefer Other Contact Methods?</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
                <Mail className="w-5 h-5 mr-2 text-[#0007b5]" />
                Direct Email
              </h3>
              <p className="text-slate-700 mb-2">
                For detailed technical inquiries or file attachments:
              </p>
              <a href="mailto:hello@create3dworks.com" className="text-[#0007b5] hover:text-[#000690] font-medium">
                hello@create3dworks.com
              </a>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-[#0007b5]" />
                Visit Our Showroom
              </h3>
              <p className="text-slate-700 mb-2">
                590 Portland St - Unit 117<br />Dartmouth, NS B2W 6B7, Canada
              </p>
              <p className="text-sm text-slate-500 mt-1">Showroom hours coming soon!</p>
            </div>
          </div>
        </div>

      </div>
    </SharedPageLayout>
  );
};
